import * as THREE from 'three';
import { Object3D } from 'three';
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _orientation = new THREE.Vector3();
/** Clamp a number to a valid float for Web Audio API (avoids "outside the range of a float" on mobile). */
function clampFloat(v, min, max) {
    if (!Number.isFinite(v))
        return 0;
    if (v < min)
        return min;
    if (v > max)
        return max;
    return v;
}
/** Clamp position components to a safe range. */
function clampPosition(p, maxAbs = 1e6) {
    p.x = clampFloat(p.x, -maxAbs, maxAbs);
    p.y = clampFloat(p.y, -maxAbs, maxAbs);
    p.z = clampFloat(p.z, -maxAbs, maxAbs);
}
/** Clamp direction vector to finite; if length is 0 or non-finite, set to fallback. */
function clampDirection(v, fallback) {
    if (!Number.isFinite(v.x))
        v.x = fallback.x;
    if (!Number.isFinite(v.y))
        v.y = fallback.y;
    if (!Number.isFinite(v.z))
        v.z = fallback.z;
    const lenSq = v.x * v.x + v.y * v.y + v.z * v.z;
    if (lenSq < 1e-12) {
        v.x = fallback.x;
        v.y = fallback.y;
        v.z = fallback.z;
    }
    else {
        const len = Math.sqrt(lenSq);
        v.x /= len;
        v.y /= len;
        v.z /= len;
    }
}
/**
 * AudioListener that clamps all values before passing to the Web Audio API.
 * Prevents "The provided value is outside the range of a float" on mobile (e.g. portrait).
 */
export default class SafeAudioListener extends THREE.AudioListener {
    constructor() {
        super(...arguments);
        this._clock = new THREE.Clock();
    }
    updateMatrixWorld(force) {
        // Update our matrix only (don't run parent's updateMatrixWorld which calls linearRampToValueAtTime with unclamped values)
        Object3D.prototype.updateMatrixWorld.call(this, force);
        const listener = this.context.listener;
        const up = this.up.clone();
        const delta = this._clock.getDelta();
        this.timeDelta = Number.isFinite(delta) ? Math.max(0, delta) : 0;
        const endTime = this.context.currentTime + this.timeDelta;
        if (!Number.isFinite(endTime))
            return;
        this.matrixWorld.decompose(_position, _quaternion, _scale);
        _orientation.set(0, 0, -1).applyQuaternion(_quaternion);
        clampPosition(_position);
        clampDirection(_orientation, new THREE.Vector3(0, 0, -1));
        clampDirection(up, new THREE.Vector3(0, 1, 0));
        if (listener.positionX) {
            listener.positionX.linearRampToValueAtTime(_position.x, endTime);
            listener.positionY.linearRampToValueAtTime(_position.y, endTime);
            listener.positionZ.linearRampToValueAtTime(_position.z, endTime);
            listener.forwardX.linearRampToValueAtTime(_orientation.x, endTime);
            listener.forwardY.linearRampToValueAtTime(_orientation.y, endTime);
            listener.forwardZ.linearRampToValueAtTime(_orientation.z, endTime);
            listener.upX.linearRampToValueAtTime(up.x, endTime);
            listener.upY.linearRampToValueAtTime(up.y, endTime);
            listener.upZ.linearRampToValueAtTime(up.z, endTime);
        }
        else {
            listener.setPosition(_position.x, _position.y, _position.z);
            listener.setOrientation(_orientation.x, _orientation.y, _orientation.z, up.x, up.y, up.z);
        }
    }
}
